import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, proposalAPI } from '../lib/api';
import { DateRangeFilter, useDateRange } from '../components/DateRangeFilter';
import { formatINR, formatINRShort, formatPercent, getMarginStatus, formatDate, getStatusBadgeClass, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  FileText,
  Building2,
  Plus,
  ArrowUpRight,
  Loader2,
  Receipt,
  BarChart3,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

// Long INR strings (₹1,13,14,334.06 = 14+ chars) must scale down instead of
// clipping the card / pushing the icon out.
const kpiSizeClass = (s) => {
  const len = (s || '').length;
  if (len > 14) return 'text-lg lg:text-xl';
  if (len > 11) return 'text-xl lg:text-2xl';
  return 'text-2xl lg:text-3xl';
};

export default function Dashboard() {
  const { can } = useAuth();
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, rangeState, setRangeState] = useDateRange('priceintel.dashboardRange', 'this_month');

  // Drill-down dialog state: which KPI was clicked + its breakdown data
  const [drill, setDrill] = useState(null); // 'revenue' | 'expenses' | 'profit' | 'margin' | null
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, revenueRes, proposalsRes] = await Promise.all([
        dashboardAPI.getStats(null, range),
        dashboardAPI.getRevenueBreakdown(null, range),
        proposalAPI.getAll(),
      ]);

      setStats(statsRes.data);
      setRevenueData(revenueRes.data);
      // Newest first — the API returns insertion order.
      const newest = [...(proposalsRes.data || [])].sort((a, b) =>
        String(b.created_at || '').localeCompare(String(a.created_at || '')));
      setProposals(newest.slice(0, 5));
    } catch (error) {
      toast.error(apiError(error, 'Failed to load the dashboard'));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchDashboardData();
    setBreakdown(null); // filters changed — stale breakdown must refetch
  }, [fetchDashboardData]);

  const openDrill = async (kpi) => {
    setDrill(kpi);
    if (!breakdown) {
      setBreakdownLoading(true);
      try {
        const res = await dashboardAPI.getProfitBreakdown(null, range);
        setBreakdown(res.data);
      } catch (error) {
        toast.error(apiError(error, 'Failed to load the breakdown'));
        setDrill(null);
      } finally {
        setBreakdownLoading(false);
      }
    }
  };

  const kpiCards = stats ? [
    {
      key: 'revenue',
      label: 'Total Revenue',
      value: formatINR(stats.total_revenue),
      icon: DollarSign,
      trend: stats.total_revenue > 0 ? 'up' : 'neutral',
      color: 'text-black',
    },
    {
      key: 'expenses',
      label: 'Total Expenses',
      value: formatINR(stats.total_expenses),
      icon: TrendingDown,
      trend: 'neutral',
      color: 'text-gray-600',
    },
    {
      key: 'profit',
      label: 'Net Profit',
      value: formatINR(stats.net_profit),
      icon: stats.net_profit >= 0 ? TrendingUp : TrendingDown,
      trend: stats.net_profit >= 0 ? 'up' : 'down',
      color: stats.net_profit >= 0 ? 'text-green-700' : 'text-red-700',
    },
    {
      key: 'margin',
      label: 'Margin',
      value: formatPercent(stats.margin_percentage),
      icon: stats.margin_percentage >= 30 ? TrendingUp : TrendingDown,
      trend: getMarginStatus(stats.margin_percentage),
      color: stats.margin_percentage >= 30 ? 'text-green-700' : stats.margin_percentage >= 20 ? 'text-orange-600' : 'text-red-700',
    },
  ] : [];

  const quickStats = stats ? [
    { label: 'Active Proposals', value: stats.active_proposals, icon: FileText, to: '/proposals' },
    { label: 'Total Clients', value: stats.total_clients, icon: Building2, to: '/clients' },
    { label: 'Team Members', value: stats.total_employees, icon: Users, to: '/employees' },
  ] : [];

  const pieColors = ['#000000', '#333333', '#555555', '#777777', '#999999'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of your business performance</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangeFilter value={rangeState} onChange={setRangeState} />
          {can('write') && (
            <Link to="/proposals/new">
              <Button data-testid="new-proposal-btn" className="bg-black hover:bg-black/90">
                <Plus className="w-4 h-4 mr-2" />
                New Proposal
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* KPI Cards — click any card for the breakdown behind the number */}
      <div className="space-y-2">
        <p className="text-xs text-gray-500">
          DESENO only. Team salaries and shared office costs aren't included (paid across both companies).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiCards.map((kpi, index) => {
            const Icon = kpi.icon;
            return (
              <Card
                key={kpi.key}
                role="button"
                tabIndex={0}
                onClick={() => openDrill(kpi.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrill(kpi.key); } }}
                className="card-hover animate-slideUp group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-500 uppercase tracking-wide">{kpi.label}</p>
                      <p
                        className={`${kpiSizeClass(kpi.value)} font-bold font-mono mt-2 leading-tight ${kpi.color}`}
                        title={kpi.value}
                      >
                        {kpi.value}
                      </p>
                    </div>
                    <div className={`shrink-0 p-2 rounded-lg ${kpi.trend === 'up' ? 'bg-green-100' : kpi.trend === 'down' ? 'bg-red-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${kpi.trend === 'up' ? 'text-green-700' : kpi.trend === 'down' ? 'text-red-700' : 'text-gray-600'}`} />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    View breakdown <ArrowUpRight className="w-3 h-3" />
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Quick Stats — each navigates to its section */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {quickStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.label} to={stat.to} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black rounded-lg">
              <Card className="card-hover h-full">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                    <Icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold font-mono">{stat.value}</p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide truncate">{stat.label}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-gray-300 ml-auto shrink-0" />
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Charts and Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Client */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Revenue by Client</CardTitle>
          </CardHeader>
          <CardContent>
            {revenueData?.by_client?.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={revenueData.by_client.slice(0, 5).map(r => ({
                    ...r,
                    // Keep full name for the tooltip, but truncate the axis label.
                    fullName: r.name,
                    name: (r.name || '').length > 22 ? (r.name || '').slice(0, 22) + '…' : r.name,
                  }))}
                  layout="vertical"
                  margin={{ left: 10, right: 24, top: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis type="number" tickFormatter={formatINRShort} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={170}
                    tick={{ fontSize: 11, fill: '#444' }}
                    interval={0}
                  />
                  <Tooltip
                    formatter={(value) => formatINR(value)}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                    contentStyle={{
                      backgroundColor: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Bar dataKey="revenue" fill="#000000" radius={[0, 4, 4, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                No revenue data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue by Payment Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Revenue by Payment Type</CardTitle>
          </CardHeader>
          <CardContent>
            {revenueData?.by_payment_terms?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={revenueData.by_payment_terms}
                    dataKey="revenue"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius="70%"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {revenueData.by_payment_terms.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatINR(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-400">
                No payment data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Proposals */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Recent Proposals</CardTitle>
          <Link to="/proposals">
            <Button variant="ghost" size="sm">
              View All
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {proposals.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th className="table-header text-left">Project</th>
                    <th className="table-header text-left">Client</th>
                    <th className="table-header text-right">Value</th>
                    <th className="table-header text-center">Margin</th>
                    <th className="table-header text-center">Status</th>
                    <th className="table-header text-left">Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((proposal) => (
                    <tr key={proposal.id} className="table-row">
                      <td className="table-cell font-medium">
                        <Link to={`/proposals/${proposal.id}`} className="hover:underline">
                          {proposal.project_name}
                        </Link>
                      </td>
                      <td className="table-cell text-gray-600">{proposal.client_name}</td>
                      <td className="table-cell text-right font-mono">{formatINR(proposal.final_price)}</td>
                      <td className="table-cell text-center">
                        <span className={`badge ${
                          proposal.margin_percentage >= 30 ? 'badge-success' :
                          proposal.margin_percentage >= 20 ? 'badge-warning' : 'badge-error'
                        }`}>
                          {formatPercent(proposal.margin_percentage)}
                        </span>
                      </td>
                      <td className="table-cell text-center">
                        <span className={`badge ${getStatusBadgeClass(proposal.status)}`}>
                          {proposal.status}
                        </span>
                      </td>
                      <td className="table-cell text-gray-600">{formatDate(proposal.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No proposals yet</p>
              {can('write') && (
                <Link to="/proposals/new">
                  <Button variant="outline" size="sm" className="mt-4">
                    Create your first proposal
                  </Button>
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI drill-down — where the money is */}
      <KpiDrillDialog
        drill={drill}
        onClose={() => setDrill(null)}
        stats={stats}
        breakdown={breakdown}
        loading={breakdownLoading}
        rangeLabel={
          range.start || range.end
            ? `${range.label} · ${range.start ? formatDate(range.start) : 'start'} – ${range.end ? formatDate(range.end) : 'today'}`
            : range.label
        }
      />
    </div>
  );
}

/* ================= KPI DRILL-DOWN ================= */

const DRILL_TITLES = {
  revenue: 'Total Revenue — where it comes from',
  expenses: 'Total Expenses — where it goes',
  profit: 'Net Profit — where the money is',
  margin: 'Margin — how it is calculated',
};

function HBarList({ rows, total, emptyText }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-gray-400 py-4 text-center">{emptyText || 'No data in this period'}</p>;
  }
  const max = Math.max(...rows.map(r => Math.abs(r.amount)), 1);
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.name} className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-baseline gap-2">
              <span className="text-sm truncate" title={r.name}>{r.name}</span>
              <span className="font-mono text-sm whitespace-nowrap">{formatINR(r.amount)}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full mt-1">
              <div className="h-1.5 bg-black rounded-full" style={{ width: `${Math.max(3, (Math.abs(r.amount) / max) * 100)}%` }} />
            </div>
          </div>
        </div>
      ))}
      {total != null && (
        <div className="flex justify-between pt-2 border-t text-sm font-medium">
          <span>Total</span>
          <span className="font-mono">{formatINR(total)}</span>
        </div>
      )}
    </div>
  );
}

function MonthlyPnlChart({ monthly }) {
  if (!monthly || monthly.length === 0) return null;
  const data = monthly.map(m => ({ ...m, label: m.month.slice(2).replace('-', '/') }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#666' }} interval={1} />
        <YAxis tickFormatter={formatINRShort} tick={{ fontSize: 10, fill: '#666' }} width={48} />
        <Tooltip
          formatter={(value, name) => [formatINR(value), name === 'revenue' ? 'Revenue' : 'Expenses']}
          labelFormatter={(l) => `Month ${l}`}
          contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '8px', color: '#fff' }}
        />
        <Legend formatter={(v) => v === 'revenue' ? 'Revenue' : 'Expenses'} wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="revenue" fill="#000000" radius={[3, 3, 0, 0]} maxBarSize={18} />
        <Bar dataKey="expenses" fill="#9ca3af" radius={[3, 3, 0, 0]} maxBarSize={18} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function KpiDrillDialog({ drill, onClose, stats, breakdown, loading, rangeLabel }) {
  if (!stats) return null;
  const open = Boolean(drill);
  const b = breakdown;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">{DRILL_TITLES[drill] || ''}</DialogTitle>
          {rangeLabel ? <p className="text-xs text-gray-400">{rangeLabel}</p> : null}
        </DialogHeader>

        {loading || !b ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6 mt-2">
            {/* The equation — always shown so the number ties out */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">Revenue</p>
                <p className="font-mono font-bold text-sm sm:text-base mt-1" title={formatINR(stats.total_revenue)}>
                  {formatINRShort(stats.total_revenue)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">− Expenses</p>
                <p className="font-mono font-bold text-sm sm:text-base mt-1" title={formatINR(stats.total_expenses)}>
                  {formatINRShort(stats.total_expenses)}
                </p>
              </div>
              <div className={`rounded-lg p-3 ${stats.net_profit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">= Net Profit</p>
                <p className={`font-mono font-bold text-sm sm:text-base mt-1 ${stats.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`} title={formatINR(stats.net_profit)}>
                  {formatINRShort(stats.net_profit)}
                </p>
              </div>
            </div>

            {drill === 'margin' && (
              <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                <p className="font-mono">Margin = Net Profit ÷ Revenue × 100 = <b>{formatPercent(stats.margin_percentage)}</b></p>
                <p className="text-xs text-gray-500">
                  ≥ 30% healthy &nbsp;·&nbsp; 20–30% watch &nbsp;·&nbsp; &lt; 20% thin — pricing or costs need attention.
                </p>
              </div>
            )}

            {(drill === 'revenue' || drill === 'profit') && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Revenue by client (top 8)</h4>
                <HBarList rows={b.revenue_by_client} total={drill === 'revenue' ? stats.total_revenue : null} />
              </div>
            )}

            {(drill === 'expenses' || drill === 'profit') && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Expenses by category (top 8)</h4>
                <HBarList rows={b.expenses_by_category?.map(c => ({ name: c.name, amount: c.amount }))} total={drill === 'expenses' ? stats.total_expenses : null} />
              </div>
            )}

            {(drill === 'profit' || drill === 'margin') && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Last 12 months</h4>
                <MonthlyPnlChart monthly={b.monthly} />
              </div>
            )}

            {/* Where to dig deeper */}
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {(drill === 'revenue' || drill === 'profit' || drill === 'margin') && (
                <Link to="/invoices" onClick={onClose}>
                  <Button variant="outline" size="sm"><FileText className="w-4 h-4 mr-1.5" /> Open Invoices</Button>
                </Link>
              )}
              {(drill === 'expenses' || drill === 'profit') && (
                <Link to="/expenses" onClick={onClose}>
                  <Button variant="outline" size="sm"><Receipt className="w-4 h-4 mr-1.5" /> Open Expenses</Button>
                </Link>
              )}
              <Link to="/financials" onClick={onClose}>
                <Button variant="outline" size="sm"><BarChart3 className="w-4 h-4 mr-1.5" /> Full Financials</Button>
              </Link>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
